import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp
} from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/DevisModal.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FirestoreDevis } from "../types/firestore";

interface DevisModalProps {
  show: boolean;
  handleClose: () => void;
  initialData?: FirestoreDevis;
  preselectedOpportunity?: any | null; // Opportunité pré-sélectionnée depuis la navigation
}

interface FirestoreOpportunity {
  contact: string;
  type: string;
  amount: number;
  date: string;
}

interface CompanyData {
  opportunities?: FirestoreOpportunity[];
  devis?: FirestoreDevis[];
  [key: string]: any;
}

const DevisModal: React.FC<DevisModalProps> = ({ show, handleClose, initialData, preselectedOpportunity }) => {
  const [companiesData, setCompaniesData] = useState<{ [key: string]: CompanyData }>({});
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedOpportunityIndex, setSelectedOpportunityIndex] = useState<number | "">("");
  const [opportunityDetails, setOpportunityDetails] = useState<FirestoreOpportunity | null>(null);

  const [form, setForm] = useState({
    contact: "",
    opportunityType: "",
    amount: "",
    date: "",
    numero: "",
    customTypeText: "",
    pdfDocumentType: "",
  });

  const [tva, setTva] = useState<number>(20);
  const [tvaIsCustom, setTvaIsCustom] = useState(false);

  const isEditing = Boolean(initialData);

  // Fetch companies data
  useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, "prospects", "companies");
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        setCompaniesData(snapshot.data() as { [key: string]: CompanyData });
      }
    };

    if (show) {
      fetchData();
      if (!initialData) {
        setSelectedCompany("");
        setSelectedOpportunityIndex("");
        setOpportunityDetails(null);
        setForm({ contact: "", opportunityType: "", amount: "", date: "", numero: "", customTypeText: "", pdfDocumentType: "" });
        setTva(20);
        setTvaIsCustom(false);
      }
    }
  }, [show, initialData]);

  // Populate form in edit mode
  useEffect(() => {
    if (initialData) {
      setSelectedCompany(initialData.companyId);
      const rawDate = initialData.date instanceof Timestamp
        ? initialData.date.toDate()
        : new Date(initialData.date);
      setForm({
        contact: initialData.contact,
        opportunityType: initialData.opportunityType,
        amount: initialData.amount.toString(),
        date: rawDate.toISOString().slice(0, 10),
        numero: initialData.id || "",
        customTypeText: (initialData as any).customTypeText || "",
        pdfDocumentType: (initialData as any).pdfDocumentType || "",
      });
      const tvaValue = (initialData as any).tva !== undefined ? (initialData as any).tva : 20;
      setTva(tvaValue);
      setTvaIsCustom(tvaValue !== 20);
    }
  }, [initialData]);

  // Pré-sélectionner l'opportunité si elle est passée en prop
  useEffect(() => {
    if (show && preselectedOpportunity && !initialData && companiesData && Object.keys(companiesData).length > 0) {
      const opp = preselectedOpportunity;
      const companyId = opp.companyId;
      
      // Vérifier que la société existe
      if (companiesData[companyId]) {
        setSelectedCompany(companyId);
        
        // Chercher l'index de l'opportunité dans le tableau
        const opportunities = companiesData[companyId].opportunities || [];
        
        // Normaliser les dates pour la comparaison
        const normalizeDate = (date: any): string => {
          if (!date) return "";
          if (date instanceof Timestamp) {
            return date.toDate().toISOString().slice(0, 10);
          }
          if (typeof date === 'string') {
            // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
            if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return date;
            }
            // Sinon, essayer de parser
            try {
              return new Date(date).toISOString().slice(0, 10);
            } catch {
              return date;
            }
          }
          return date;
        };
        
        const oppDateNormalized = normalizeDate(opp.date);
        
        const oppIndex = opportunities.findIndex((o: any) => {
          const oDateNormalized = normalizeDate(o.date);
          return (
            o.type === opp.type && 
            o.contact === opp.contact &&
            Math.abs((o.amount || 0) - (opp.amount || 0)) < 0.01 && // Comparaison avec tolérance pour les nombres
            oDateNormalized === oppDateNormalized
          );
        });
        
        if (oppIndex >= 0) {
          setSelectedOpportunityIndex(oppIndex);
        } else {
          // Si l'opportunité n'est pas trouvée par index, pré-remplir directement les champs
          const dateValue = oppDateNormalized || (opp.date ? normalizeDate(opp.date) : "");
          setForm({
            contact: opp.contact || "",
            opportunityType: opp.type || "",
            amount: opp.amount?.toString() || "",
            date: dateValue,
            numero: "",
            customTypeText: "",
            pdfDocumentType: "",
          });
        }
      }
    }
  }, [show, preselectedOpportunity, initialData, companiesData]);

  // Update opportunity details when selected
  useEffect(() => {
    if (
      selectedCompany &&
      selectedOpportunityIndex !== "" &&
      companiesData[selectedCompany]?.opportunities
    ) {
      const index = Number(selectedOpportunityIndex);
      const selectedOpp = companiesData[selectedCompany].opportunities![index];
      setOpportunityDetails(selectedOpp);
      setForm({
        contact: selectedOpp.contact,
        opportunityType: selectedOpp.type,
        amount: selectedOpp.amount.toString(),
        date: selectedOpp.date,
        numero: "",
        customTypeText: "",
        pdfDocumentType: "",
      });
    }
  }, [selectedCompany, selectedOpportunityIndex, companiesData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Generate global Devis ID au format D260101 (année + mois + numéro dans le mois)
  const generateGlobalDevisId = async (dateString: string): Promise<string> => {
    const docRef = doc(db, "prospects", "companies");
    const snapshot = await getDoc(docRef);
    
    // Parser la date pour extraire année et mois
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(-2); // 2 derniers chiffres de l'année (ex: 26 pour 2026)
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Mois sur 2 chiffres (01-12)
    
    if (!snapshot.exists()) {
      // Premier devis du mois : D260101
      return `D${year}${month}01`;
    }

    const data = snapshot.data();
    const allDevis: FirestoreDevis[] = [];
    Object.values(data).forEach((company: any) => {
      if (company.devis && Array.isArray(company.devis)) {
        allDevis.push(...company.devis);
      }
    });

    // Filtrer les devis du même mois et année
    const prefix = `D${year}${month}`;
    const sameMonthDevis = allDevis.filter(d => {
      if (!d.id || !d.id.startsWith(prefix)) return false;
      
      // Vérifier aussi la date du devis pour être sûr
      if (d.date) {
        const devisDate = d.date instanceof Timestamp ? d.date.toDate() : new Date(d.date);
        const devisYear = devisDate.getFullYear().toString().slice(-2);
        const devisMonth = (devisDate.getMonth() + 1).toString().padStart(2, '0');
        return devisYear === year && devisMonth === month;
      }
      return true;
    });

    // Extraire les numéros de devis du mois (les 2 derniers chiffres après le préfixe)
    const existingNums = sameMonthDevis
      .map(d => {
        if (!d.id || !d.id.startsWith(prefix)) return null;
        // Le numéro est les 2 derniers chiffres après D + année + mois
        const numStr = d.id.slice(prefix.length);
        const num = parseInt(numStr, 10);
        return isNaN(num) ? null : num;
      })
      .filter((n): n is number => n !== null);

    // Trouver le prochain numéro disponible
    const max = existingNums.length > 0 ? Math.max(...existingNums) : 0;
    const next = (max + 1).toString().padStart(2, '0'); // Numéro sur 2 chiffres (01-99)
    
    return `${prefix}${next}`;
  };

  const handleSave = async () => {
    if (!selectedCompany || !form.contact || !form.opportunityType || !form.amount || !form.date) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    try {
      const docRef = doc(db, "prospects", "companies");
      const snapshot = await getDoc(docRef);
      
      if (!snapshot.exists()) {
        toast.error("Société introuvable.");
        return;
      }

      const data = snapshot.data();
      const devisList = data[selectedCompany]?.devis || [];
      const dateTimestamp = Timestamp.fromDate(new Date(form.date));
      const updatedAt = Timestamp.now();

      const newDevis: FirestoreDevis = {
        id: form.numero || await generateGlobalDevisId(form.date),
        companyId: selectedCompany,
        contact: form.contact,
        opportunityType: form.opportunityType,
        amount: parseFloat(form.amount),
        date: dateTimestamp,
        updatedAt,
        ...(form.customTypeText && { customTypeText: form.customTypeText }), // Ajouter seulement si non vide
        ...(form.pdfDocumentType && { pdfDocumentType: form.pdfDocumentType }),
        tva: tva,
      };

      const updatedList = isEditing && initialData?.id
        ? devisList.map((d: any) => d.id === initialData.id ? newDevis : d)
        : [...devisList, newDevis];

      await updateDoc(docRef, {
        [`${selectedCompany}.devis`]: updatedList
      });

      toast.success(isEditing ? "Devis mis à jour." : "Nouveau devis enregistré.");
      setTimeout(handleClose, 1500);
    } catch (err) {
      console.error("Erreur lors de l'enregistrement du devis:", err);
      toast.error("Une erreur est survenue lors de l'enregistrement.");
    }
  };

  if (!show) return null;

  // Companies with opportunities only
  const companyNamesWithOpportunities = Object.keys(companiesData)
    .filter(name => (companiesData[name]?.opportunities?.length ?? 0) > 0)
    .sort((a, b) => a.localeCompare(b));
  const opportunities = selectedCompany
    ? companiesData[selectedCompany]?.opportunities || []
    : [];

  return (
    <>
      <ToastContainer position="top-right" autoClose={2000} />
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>{isEditing ? "Modifier un Devis" : "Ajouter un Devis"}</h3>

          <div className="form-group">
            <label>Numéro de devis (optionnel - généré automatiquement si vide)</label>
            <input
              type="text"
              name="numero"
              value={form.numero}
              onChange={handleChange}
              placeholder="Ex: D260101 (généré automatiquement)"
            />
          </div>

          <div className="form-group">
            <label>Société</label>
            <select
              value={selectedCompany}
              onChange={e => setSelectedCompany(e.target.value)}
              disabled={isEditing}
            >
              <option value="">-- Choisir une société --</option>
              {companyNamesWithOpportunities.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {!isEditing && selectedCompany && opportunities.length > 0 && (
            <div className="form-group">
              <label>Opportunité</label>
              <select
                value={selectedOpportunityIndex}
                onChange={e => setSelectedOpportunityIndex(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">-- Choisir une opportunité --</option>
                {opportunities.map((opp, idx) => (
                  <option key={idx} value={idx}>
                    {opp.type} – {opp.date}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>Contact</label>
              <input
                type="text"
                name="contact"
                value={form.contact}
                onChange={handleChange}
                disabled={!isEditing && opportunityDetails !== null}
              />
            </div>
            <div className="form-group">
              <label>Type d'opportunité</label>
              <input
                type="text"
                name="opportunityType"
                value={form.opportunityType}
                onChange={handleChange}
                disabled={!isEditing && opportunityDetails !== null}
              />
            </div>
            <div className="form-group">
              <label>Texte du type pour le PDF (optionnel)</label>
              <input
                type="text"
                name="customTypeText"
                value={form.customTypeText}
                onChange={handleChange}
                placeholder={`Par défaut: ${form.opportunityType}`}
                title="Ce texte remplacera le type d'opportunité sur le PDF généré"
              />
              <small style={{ fontSize: "0.85em", color: "#666", display: "block", marginTop: "4px" }}>
                Laissez vide pour utiliser le type d'opportunité par défaut
              </small>
            </div>
            <div className="form-group">
              <label>Type de document PDF</label>
              <select
                name="pdfDocumentType"
                value={form.pdfDocumentType}
                onChange={handleChange}
              >
                <option value="">-- Choisir un document --</option>
                <option value="Devis REC TTB">Devis REC TTB</option>
                <option value="Devis REC FDA Enregistrement + Gestion">Devis REC FDA Enregistrement + Gestion</option>
                <option value="Devis REC FDA Gestion">Devis REC FDA Gestion</option>
                <option value="Devis REC Conseil réglementaire">Devis REC Conseil réglementaire</option>
                <option value="Devis REC CIFER China Registration">Devis REC CIFER China Registration</option>
              </select>
              <small style={{ fontSize: "0.85em", color: "#666", display: "block", marginTop: "4px" }}>
                Document PDF utilisé pour générer le devis
              </small>
            </div>
            <div className="form-group">
              <label>Montant HT</label>
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                disabled={!isEditing && opportunityDetails !== null}
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={tvaIsCustom}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTvaIsCustom(checked);
                  if (!checked) setTva(20);
                }}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              TVA différente de 20%
            </label>
            {tvaIsCustom && (
              <input
                type="number"
                value={tva}
                onChange={(e) => setTva(parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="0.1"
                placeholder="Ex: 0"
                style={{ marginTop: '8px' }}
              />
            )}
            {form.amount && (
              <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
                {tva === 0
                  ? `TVA 0% — Montant TTC = HT : ${parseFloat(form.amount).toFixed(2)}€`
                  : `Montant TTC : ${parseFloat(form.amount).toFixed(2)}€ × ${(1 + tva / 100).toFixed(2)} (TVA ${tva}%) = ${(parseFloat(form.amount) * (1 + tva / 100)).toFixed(2)}€`
                }
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              disabled={!isEditing && opportunityDetails !== null}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleSave}> {isEditing ? "Mettre à jour" : "Créer"} </button>
            <button type="button" onClick={handleClose}>Annuler</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DevisModal;
